import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Save, Trash2, AlertTriangle, CheckCircle2, X, Upload, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CarryoverPanel({ projectId, onClose }) {
  const [entries, setEntries] = useState([]);
  const [fromYear, setFromYear] = useState(2025);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [coRes, periodRes] = await Promise.all([
        axios.get(`${API}/api/mdp/carryover/${projectId}?from_year=${fromYear}`),
        axios.get(`${API}/api/mdp/period-data?project_id=${projectId}&start_date=2026-01-01&end_date=2026-12-31`),
      ]);

      const materialList = Object.values(periodRes.data?.materials || {})
        .sort((a, b) => (a.description || '').localeCompare(b.description || ''));
      
      const coMap = {};
      (coRes.data?.entries || []).forEach(e => {
        coMap[e.customer_material_code] = e.quantity;
      });

      const merged = materialList.map(m => ({
        customer_material_code: m.customer_material_code,
        kiel_material_code: m.kiel_material_code || '',
        description: m.description,
        spu: m.sittings_per_unit || 0,
        component_type: m.component_type,
        quantity: coMap[m.customer_material_code] ?? 0,
        total_mdp: m.total_mdp || 0,
      }));
      setEntries(merged);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load carry-over data');
    }
    setLoading(false);
  }, [projectId, fromYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (idx, val) => {
    setEntries(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], quantity: parseInt(val) || 0 };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        from_year: fromYear,
        entries: entries.filter(e => e.quantity !== 0).map(e => ({
          customer_material_code: e.customer_material_code,
          description: e.description,
          quantity: e.quantity,
        })),
      };
      const res = await axios.post(`${API}/api/mdp/carryover/${projectId}`, payload);
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Save failed');
    }
    setSaving(false);
  };

  const handleClear = async () => {
    if (!window.confirm(`Clear all carry-over entries from ${fromYear}?`)) return;
    try {
      await axios.delete(`${API}/api/mdp/carryover/${projectId}?from_year=${fromYear}`);
      toast.success('Carry-over cleared');
      setEntries(prev => prev.map(e => ({ ...e, quantity: 0 })));
    } catch (err) {
      toast.error('Clear failed');
    }
  };

  // Download Excel template
  const downloadTemplate = () => {
    const BOM = '\uFEFF';
    let csv = BOM + 'customer_material_code,description,kiel_material_code,type,mdp_2026,carryover_quantity\n';
    entries.forEach(e => {
      const type = e.spu === 2 ? '2P' : e.spu === 1 ? '1P' : (e.component_type === 'seat' ? 'seat' : 'other');
      csv += `${e.customer_material_code},"${e.description}",${e.kiel_material_code},${type},${e.total_mdp},${e.quantity}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `carryover_${fromYear}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded - fill carryover_quantity column and upload back');
  };

  // Upload Excel/CSV with carry-over values
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const text = evt.target.result;
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length < 2) { toast.error('Empty file'); return; }

        // Parse header to find columns
        const header = lines[0].toLowerCase().replace(/\ufeff/g, '');
        const cols = header.split(',').map(c => c.trim().replace(/"/g, ''));
        const codeIdx = cols.findIndex(c => c.includes('customer_material_code') || c.includes('code') || c.includes('tc'));
        const qtyIdx = cols.findIndex(c => c.includes('carryover') || c.includes('carry') || c.includes('quantity') || c.includes('qty'));
        
        if (codeIdx === -1 || qtyIdx === -1) {
          toast.error('CSV must have customer_material_code and carryover_quantity columns');
          return;
        }

        let updated = 0;
        const coMap = {};
        for (let i = 1; i < lines.length; i++) {
          // Parse CSV line (handle quoted values)
          const parts = [];
          let current = '';
          let inQuote = false;
          for (const ch of lines[i]) {
            if (ch === '"') { inQuote = !inQuote; continue; }
            if (ch === ',' && !inQuote) { parts.push(current.trim()); current = ''; continue; }
            current += ch;
          }
          parts.push(current.trim());

          const code = parts[codeIdx]?.trim();
          const qty = parseInt(parts[qtyIdx]) || 0;
          if (code) coMap[code] = qty;
        }

        setEntries(prev => prev.map(e => {
          if (coMap[e.customer_material_code] !== undefined) {
            updated++;
            return { ...e, quantity: coMap[e.customer_material_code] };
          }
          return e;
        }));

        toast.success(`${updated} materials updated from file. Click "Save" to apply.`);
      } catch (err) {
        toast.error('Failed to parse file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const seatEntries = entries.filter(e => e.component_type === 'seat');
  const otherEntries = entries.filter(e => e.component_type !== 'seat');
  const totalCarryover = entries.reduce((s, e) => s + e.quantity, 0);
  const totalSeatPlaced = entries.reduce((s, e) => s + e.quantity * (e.spu || 0), 0);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto" />
        <p className="text-sm text-slate-500 mt-3">Loading...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="carryover-panel">
      {/* Header */}
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-800">Carry-over from {fromYear}</h3>
          <p className="text-[11px] text-slate-500">
            Shortage (+) or surplus (-) from {fromYear} → affects {fromYear + 1} calculations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={downloadTemplate}
            className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200"
            data-testid="carryover-download-btn">
            <Download className="w-3.5 h-3.5" /> Download Template
          </button>
          <button onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="carryover-upload-btn">
            <Upload className="w-3.5 h-3.5" /> Upload CSV
          </button>
          <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFileUpload} className="hidden" />
          <select value={fromYear} onChange={e => setFromYear(parseInt(e.target.value))}
            className="border rounded-lg px-2 py-1.5 text-xs" data-testid="carryover-year-select">
            <option value={2024}>2024</option>
            <option value={2025}>2025</option>
          </select>
          {onClose && <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>}
        </div>
      </div>

      {/* Summary */}
      <div className="px-5 py-2 bg-slate-50/50 border-b border-slate-100 flex items-center gap-4 text-sm">
        {totalCarryover !== 0 ? (
          totalCarryover > 0 ? <AlertTriangle className="w-4 h-4 text-red-500" /> : <CheckCircle2 className="w-4 h-4 text-green-500" />
        ) : null}
        <span className="text-slate-600">
          Total: <span className={`font-bold ${totalCarryover > 0 ? 'text-red-600' : totalCarryover < 0 ? 'text-green-600' : 'text-slate-400'}`}>
            {totalCarryover > 0 ? '+' : ''}{totalCarryover} seats
          </span>
        </span>
        {totalSeatPlaced !== 0 && (
          <span className="text-slate-500">= <span className="font-semibold">{totalSeatPlaced > 0 ? '+' : ''}{totalSeatPlaced} seat placed</span></span>
        )}
      </div>

      {/* Table */}
      <div className="max-h-[50vh] overflow-y-auto">
        {/* Seats */}
        {seatEntries.length > 0 && (
          <>
            <div className="px-5 py-1.5 bg-blue-50/50 border-b border-blue-100 sticky top-0">
              <span className="text-[10px] font-bold text-blue-600 uppercase">Seats ({seatEntries.length})</span>
            </div>
            {seatEntries.map((e, idx) => {
              const realIdx = entries.indexOf(e);
              return (
                <div key={e.customer_material_code} className="flex items-center gap-3 px-5 py-2 border-b border-slate-50 hover:bg-slate-50/50" data-testid={`carryover-row-${e.customer_material_code}`}>
                  <div className="min-w-[240px] flex-shrink-0">
                    <div className="text-[13px] font-semibold text-slate-700">{e.description}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[10px] text-slate-400 font-mono">{e.customer_material_code}</span>
                      <span className="text-[10px] text-slate-300 font-mono">{e.kiel_material_code}</span>
                      <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1 rounded">{e.spu === 2 ? '2P' : '1P'}</span>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 w-16 text-right font-medium">{e.total_mdp}</div>
                  <input type="number" value={e.quantity} onChange={ev => handleChange(realIdx, ev.target.value)}
                    className={`w-20 border rounded px-2 py-1 text-center text-sm font-bold focus:outline-none focus:ring-2 ${
                      e.quantity > 0 ? 'border-red-300 text-red-600 focus:ring-red-400 bg-red-50/50' :
                      e.quantity < 0 ? 'border-green-300 text-green-600 focus:ring-green-400 bg-green-50/50' :
                      'border-slate-200 text-slate-500 focus:ring-blue-400'}`}
                    data-testid={`carryover-input-${e.customer_material_code}`} />
                  {e.quantity !== 0 && (
                    <span className={`text-[11px] font-semibold w-16 ${e.quantity > 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {e.quantity > 0 ? '+' : ''}{e.quantity * e.spu} sp
                    </span>
                  )}
                </div>
              );
            })}
          </>
        )}
        {/* Others */}
        {otherEntries.length > 0 && (
          <>
            <div className="px-5 py-1.5 bg-slate-50 border-b border-slate-100 sticky top-0">
              <span className="text-[10px] font-bold text-slate-500 uppercase">Others ({otherEntries.length})</span>
            </div>
            {otherEntries.map((e) => {
              const realIdx = entries.indexOf(e);
              return (
                <div key={e.customer_material_code} className="flex items-center gap-3 px-5 py-2 border-b border-slate-50 hover:bg-slate-50/50">
                  <div className="min-w-[240px] flex-shrink-0">
                    <div className="text-[13px] font-semibold text-slate-700">{e.description}</div>
                    <span className="text-[10px] text-slate-400 font-mono">{e.customer_material_code} {e.kiel_material_code}</span>
                  </div>
                  <div className="text-xs text-slate-500 w-16 text-right font-medium">{e.total_mdp}</div>
                  <input type="number" value={e.quantity} onChange={ev => handleChange(realIdx, ev.target.value)}
                    className={`w-20 border rounded px-2 py-1 text-center text-sm font-bold focus:outline-none focus:ring-2 ${
                      e.quantity > 0 ? 'border-red-300 text-red-600 focus:ring-red-400 bg-red-50/50' :
                      e.quantity < 0 ? 'border-green-300 text-green-600 focus:ring-green-400 bg-green-50/50' :
                      'border-slate-200 text-slate-500 focus:ring-blue-400'}`} />
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
        <button onClick={handleClear}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg"
          data-testid="carryover-clear-btn">
          <Trash2 className="w-3.5 h-3.5" /> Clear All
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1 px-5 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          data-testid="carryover-save-btn">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Carry-over'}
        </button>
      </div>
    </div>
  );
}
